/* Shared cart/drawer shipping timing and serialized cart-note persistence. */
(function () {
  'use strict';
  const ASAP = 'Ship As Soon As Possible';
  const HOLD = 'Hold Until Date';
  const START = '[BG Shipping Hold]';
  const END = '[/BG Shipping Hold]';
  const blockPattern = /\[BG Shipping Hold\][\s\S]*?\[\/BG Shipping Hold\]/g;

  function customerNote(note) {
    return String(note || '').replace(blockPattern, '').trim();
  }

  function validDate(value, today) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value) || value < today) return false;
    const parsed = new Date(value + 'T12:00:00Z');
    return !Number.isNaN(parsed.valueOf()) && parsed.toISOString().slice(0, 10) === value;
  }

  function mergedNote(note, preference, value) {
    const text = customerNote(note);
    if (preference === ASAP) return text;
    return `${START}\nHOLD UNTIL: ${value}\n${END}` + (text ? `\n\n${text}` : '');
  }

  function createController({ read, write, today, changed = () => {} }) {
    const state = { preference: ASAP, date: '', note: '', ready: false, error: '', saving: 0 };
    let queue = Promise.resolve();
    let revision = 0;
    const ready = read().then(cart => {
      const attrs = cart.attributes || {};
      state.preference = attrs['Shipping Preference'] || (attrs['Hold Until Date'] ? HOLD : ASAP);
      state.date = attrs['Hold Until Date'] || '';
      state.note = customerNote(cart.note);
      state.ready = true;
      changed(state);
    }).catch(error => {
      state.error = 'Shipping preferences could not be loaded. Please refresh and try again.';
      changed(state);
      throw error;
    });
    // The checkout handler still awaits the original rejecting promise.
    ready.catch(() => {});

    function validation() {
      if (![ASAP, HOLD].includes(state.preference)) return 'Please choose a shipping preference.';
      if (state.preference === HOLD && !validDate(state.date, today())) {
        return 'Please choose today or a future date to hold your order.';
      }
      return '';
    }

    function set(values) {
      Object.assign(state, values, { error: '' });
      revision++;
      changed(state);
    }

    async function save() {
      await ready;
      const error = validation();
      if (error) {
        state.error = error;
        changed(state);
        throw new Error(error);
      }
      const savedRevision = revision;
      const value = state.preference === HOLD ? state.date : '';
      const body = {
        attributes: { 'Shipping Preference': state.preference, 'Hold Until Date': value },
        note: mergedNote(state.note, state.preference, value),
      };
      const operation = queue.catch(() => {}).then(async () => {
        const cart = await write(body);
        const attrs = cart.attributes || {};
        if ((cart.note || '') !== body.note ||
            attrs['Shipping Preference'] !== body.attributes['Shipping Preference'] ||
            (attrs['Hold Until Date'] || '') !== value) {
          throw new Error('Shipping preference readback failed');
        }
        return cart;
      });
      queue = operation;
      state.saving++;
      changed(state);
      try {
        const cart = await operation;
        if (savedRevision === revision) {
          state.error = '';
          changed(state);
        }
        return cart;
      } catch (error) {
        state.error = 'Your shipping preference could not be saved. Please try again before checking out.';
        changed(state);
        throw error;
      } finally {
        state.saving--;
        changed(state);
      }
    }

    async function flush() {
      // Include edits made while an earlier save is still in flight.
      let savedRevision;
      do {
        savedRevision = revision;
        await save();
      } while (savedRevision !== revision);
    }

    return { state, ready, set, save, flush, validation };
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { ASAP, HOLD, customerNote, validDate, mergedNote, createController };
    return;
  }
  if (window.BGShippingHold) return;
  const root = window.Shopify?.routes?.root || '/';
  const controls = () => [...document.querySelectorAll('bg-shipping-hold')];
  function today() {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Chicago', year: 'numeric', month: '2-digit', day: '2-digit',
    }).formatToParts(new Date());
    const get = type => parts.find(part => part.type === type).value;
    return `${get('year')}-${get('month')}-${get('day')}`;
  }
  async function cartRequest(path, body) {
    const response = await fetch(root + path, body ? {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    } : { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Cart request failed');
    return response.json();
  }
  let controller;
  function refresh() {
    if (!controller) return;
    const state = controller.state;
    controls().forEach(control => control.render());
    document.querySelectorAll('[data-bg-shipping-express]').forEach(element => {
      const estimates = window.BGLeadtimeCapture?.summaryState;
      element.hidden = !state.ready || state.preference === HOLD || state.saving > 0 || !!state.error ||
        !!(estimates && (!estimates.ready || estimates.pending || estimates.error));
    });
    document.querySelectorAll('cart-note textarea').forEach(textarea => {
      if (textarea !== document.activeElement) textarea.value = state.note;
    });
  }
  function getController() {
    if (!controller) controller = createController({
      read: () => cartRequest('cart.js'), write: body => cartRequest('cart/update.js', body),
      today, changed: refresh,
    });
    return controller;
  }

  document.addEventListener('bg:leadtime-summary', refresh);

  class ShippingHold extends HTMLElement {
    connectedCallback() {
      if (this.initialized) { this.render(); return; }
      this.initialized = true;
      this.addEventListener('change', event => {
        const model = getController();
        if (event.target.matches('[data-shipping-preference]')) {
          model.set({ preference: event.target.value });
        } else if (event.target.matches('[data-hold-date]')) {
          model.set({ date: event.target.value });
        } else return;
        if (!model.validation()) model.save().catch(() => {});
      });
      getController();
      this.render();
    }

    render() {
      if (!this.querySelector('fieldset')) return;
      const state = getController().state;
      this.querySelector('fieldset').disabled = !state.ready;
      this.querySelectorAll('[data-shipping-preference]').forEach(input => {
        input.checked = input.value === state.preference;
      });
      const date = this.querySelector('[data-hold-date]');
      date.min = today();
      date.value = state.date;
      date.required = state.preference === HOLD;
      this.querySelector('[data-hold-date-row]').hidden = state.preference !== HOLD;
      this.querySelector('[data-shipping-error]').textContent = state.error;
      this.querySelector('[data-shipping-error]').hidden = !state.error;
    }
  }
  customElements.define('bg-shipping-hold', ShippingHold);
  window.BGShippingHold = {
    async saveNote(note) {
      const model = getController();
      await model.ready;
      model.set({ note: customerNote(note) });
      return model.save();
    },
  };

  // Dwell morphs existing section children after quantity changes and first add-to-cart.
  new MutationObserver(records => {
    const relevant = records.some(record => [...record.addedNodes].some(node =>
      node.nodeType === 1 && (node.matches('bg-shipping-hold,[data-bg-shipping-express],cart-note') ||
        node.querySelector('bg-shipping-hold,[data-bg-shipping-express],cart-note') ||
        node.closest('bg-shipping-hold'))));
    if (relevant) refresh();
  }).observe(document.body, { childList: true, subtree: true });

  // Capture raw input before the theme's debounced note save, including a rapid checkout click.
  document.addEventListener('input', event => {
    if (event.target.matches('cart-note textarea')) {
      const model = getController();
      if (model.state.ready) model.set({ note: customerNote(event.target.value) });
    }
  }, true);

  let resuming = false;
  let checkingOut = false;
  async function checkout(event, resume) {
    if (resuming || !document.querySelector('bg-shipping-hold')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (checkingOut) return;
    checkingOut = true;
    const model = getController();
    try {
      await model.flush();
      if (window.BGLeadtimeCapture) await window.BGLeadtimeCapture.prepareOrderSummary();
      resuming = true;
      resume();
    } catch (_) {
      if (!model.state.error) {
        model.state.error = 'We could not prepare checkout. Please try again.';
        refresh();
      }
      const control = controls().find(element => element.getClientRects().length);
      control?.querySelector('[data-shipping-error]')?.scrollIntoView({ block: 'nearest' });
      control?.querySelector('[data-hold-date]')?.focus();
    } finally {
      resuming = false;
      checkingOut = false;
    }
  }
  document.addEventListener('click', event => {
    const button = event.target.closest('button[name="checkout"],a[href*="/checkout"]');
    if (button) checkout(event, () => button.click());
  }, true);
  document.addEventListener('submit', event => {
    if (event.submitter?.name === 'checkout') {
      checkout(event, () => event.target.requestSubmit(event.submitter));
    }
  }, true);
})();
